import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { getToofiModeMeta } from "@/lib/toofi-modes";
import { cn } from "@/lib/utils";
import type { ReaderAiThread } from "@/types/agentic-chat";
import { Clock3, CopyPlus, MoreHorizontal, Pencil, Pin, PinOff, RotateCcw, Trash2 } from "lucide-react";

type ToofiSidebarThreadItemProps = {
  thread: ReaderAiThread;
  active?: boolean;
  compact?: boolean;
  showScopeLabel?: boolean;
  onSelect: (threadId: string) => void;
  onRequestRename: (thread: ReaderAiThread) => void;
  onRequestDelete: (thread: ReaderAiThread) => void;
  onPinThread?: (threadId: string) => void;
  onUnpinThread?: (threadId: string) => void;
  onArchiveThread?: (threadId: string) => void;
  onRestoreThread?: (threadId: string) => void;
  onStartSimilarChat?: (threadId: string) => void;
  onSetThreadMemoryMode?: (threadId: string, mode: "default" | "temporary") => void;
};

const formatSurfaceLabel = (surfaceOrigin?: ReaderAiThread["surfaceOrigin"]) =>
  surfaceOrigin ? surfaceOrigin.replace(/_/g, " ") : null;

const formatDateLabel = (updatedAt: number) =>
  new Date(updatedAt).toLocaleDateString(undefined, { month: "short", day: "numeric" });

export function ToofiSidebarThreadItem({
  thread,
  active = false,
  compact = false,
  showScopeLabel = false,
  onSelect,
  onRequestRename,
  onRequestDelete,
  onPinThread,
  onUnpinThread,
  onArchiveThread,
  onRestoreThread,
  onStartSimilarChat,
  onSetThreadMemoryMode,
}: ToofiSidebarThreadItemProps) {
  const scopeLabel = thread.scope === "workspace" ? "Workspace" : "Reader";
  const metaParts = [
    thread.currentProductMode ? getToofiModeMeta(thread.currentProductMode).shortLabel : null,
    formatSurfaceLabel(thread.surfaceOrigin),
    formatDateLabel(thread.updatedAt),
  ].filter(Boolean) as string[];

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(thread.id)}
      onKeyDown={(event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        onSelect(thread.id);
      }}
      className={cn(
        "group relative flex w-full items-start gap-2 rounded-[1rem] border px-2.5 py-2.5 text-left transition-all duration-150 active:scale-[0.99] touch-manipulation",
        compact ? "gap-2 py-2.5" : "gap-2.5 py-2.5",
        active
          ? "border-blue-200/85 bg-[linear-gradient(180deg,rgba(239,246,255,0.92),rgba(219,234,254,0.84))] shadow-[inset_0_1px_0_rgba(255,255,255,0.72),0_10px_22px_rgba(37,99,235,0.08)]"
          : "border-white/80 bg-[linear-gradient(180deg,rgba(255,255,255,0.84),rgba(248,250,252,0.72))] shadow-[inset_0_1px_0_rgba(255,255,255,0.72)] hover:border-slate-200/85 hover:bg-white/92 hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.72),0_8px_18px_rgba(148,163,184,0.06)]",
      )}
    >
      <div className="min-w-0 flex-1 pr-8">
        <div className="flex min-w-0 items-center gap-2">
          {thread.pinned ? <Pin className="h-3.5 w-3.5 shrink-0 text-blue-600" /> : null}
          <p className="min-w-0 flex-1 truncate text-[12px] font-semibold tracking-[-0.02em] text-slate-900">{thread.title}</p>
        </div>
        <div className="mt-1 flex min-w-0 flex-wrap items-center gap-1.5 text-[10px] font-medium uppercase tracking-[0.1em] text-slate-400">
          {showScopeLabel ? (
            <span
              className={cn(
                "inline-flex shrink-0 items-center rounded-full border px-1.5 py-0.5 text-[9px] font-semibold tracking-[0.08em]",
                thread.scope === "workspace"
                  ? "border-blue-200/80 bg-blue-50/90 text-blue-700"
                  : "border-slate-200/85 bg-white/90 text-slate-500",
              )}
            >
              {scopeLabel}
            </span>
          ) : null}
          <span className="min-w-0 max-w-full flex-1 truncate">{metaParts.join(" / ") || "Chat"}</span>
          {thread.memoryMode === "temporary" ? (
            <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-amber-200/80 bg-amber-50/90 px-1.5 py-0.5 text-[9px] font-semibold tracking-[0.08em] text-amber-700">
              <Clock3 className="h-2.5 w-2.5" />
              Temp
            </span>
          ) : null}
        </div>
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={`Open actions for ${thread.title}`}
            className={cn(
              "absolute right-1.5 top-1.5 h-8 w-8 rounded-full text-slate-400 shadow-none hover:bg-white/90 hover:text-slate-700",
              compact ? "opacity-100" : "opacity-100 lg:opacity-0 lg:group-hover:opacity-100 lg:group-focus-within:opacity-100",
            )}
            onClick={(event) => event.stopPropagation()}
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" sideOffset={12} className="w-48 rounded-[1.15rem] border border-slate-200/75 bg-white/96 p-1.5 backdrop-blur-2xl">
          <DropdownMenuItem onSelect={() => (thread.pinned ? onUnpinThread?.(thread.id) : onPinThread?.(thread.id))}>
            {thread.pinned ? <PinOff className="mr-2 h-4 w-4" /> : <Pin className="mr-2 h-4 w-4" />}
            {thread.pinned ? "Unpin" : "Pin"}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => onStartSimilarChat?.(thread.id)}>
            <CopyPlus className="mr-2 h-4 w-4" />
            Start similar chat
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() =>
              onSetThreadMemoryMode?.(thread.id, thread.memoryMode === "temporary" ? "default" : "temporary")
            }
          >
            <Clock3 className="mr-2 h-4 w-4" />
            {thread.memoryMode === "temporary" ? "Use memory" : "Temporary chat"}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => (thread.archived ? onRestoreThread?.(thread.id) : onArchiveThread?.(thread.id))}>
            <RotateCcw className="mr-2 h-4 w-4" />
            {thread.archived ? "Restore" : "Archive"}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => onRequestRename(thread)}>
            <Pencil className="mr-2 h-4 w-4" />
            Rename
          </DropdownMenuItem>
          <DropdownMenuItem className="text-rose-600 focus:text-rose-700" onSelect={() => onRequestDelete(thread)}>
            <Trash2 className="mr-2 h-4 w-4" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
